import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, useTheme } from "@mui/material";
import Icon from "../../../Icon/Icon";
import { distinctValues } from "../utils/pivot";

// A leading glyph + accent color per drop zone, so each is recognizable at a
// glance. Colors mirror the per-zone accents in Builder.scss.
const ZONE_ICON = {
  rows: { name: "outline_row_vertical", color: "#7c4dff" },
  cols: { name: "outline_row_horizontal", color: "#0e9488" },
  filters: { name: "outline_colorfilter", color: "#d97706" },
};

/**
 * Header field pane: a source of draggable fields (split into report Fields and
 * Catalogs) plus the drop zones (Rows / Columns / Filters). The value is fixed
 * (Count/%) and set from the view bar, so there is no Values zone here.
 * Mirrors Excel's PivotTable field pane.
 *
 * Catalog chips carry a depth stepper (hierarchy level), since depth is a
 * per-catalog setting; it rides with the chip wherever it is placed.
 */
const DepthStepper = ({ value, onChange, title }) => {
  const clamp = (n) => Math.max(1, Math.min(4, n));
  const step = (e, delta) => {
    e.stopPropagation();
    onChange(clamp(value + delta));
  };
  return (
    <span
      className="pivot-depth"
      draggable={false}
      onDragStart={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      title={title}
    >
      <button
        type="button"
        className="pivot-depth__btn"
        onClick={(e) => step(e, -1)}
        disabled={value <= 1}
        aria-label="-"
      >
        −
      </button>
      <span className="pivot-depth__val">{value}</span>
      <button
        type="button"
        className="pivot-depth__btn"
        onClick={(e) => step(e, 1)}
        disabled={value >= 4}
        aria-label="+"
      >
        +
      </button>
    </span>
  );
};

const chipVariant = (field) =>
  field.isCatalog
    ? "catalog"
    : field.isIndicator
    ? "indicator"
    : field.isTagFlag
    ? "flag"
    : "";

// Leading glyph: indicators reuse their chosen icon; flags reuse the colored
// flag used everywhere tags appear (color = theme.palette[tagKey]).
const ChipIcon = ({ field }) => {
  const theme = useTheme();
  if (field.isIndicator && field.iconName)
    return <Icon name={field.iconName} size={14} className="pivot-chip__icon" />;
  if (field.isTagFlag)
    return (
      <Icon
        name="bold_flag"
        size={14}
        color={theme.palette?.[field.tagKey]?.main || theme.palette.secondary.main}
        className="pivot-chip__icon"
      />
    );
  return null;
};

const FieldChip = ({
  field,
  origin,
  onDragStart,
  onRemove,
  depth,
  onChangeDepth,
  depthTitle,
}) => {
  const variant = chipVariant(field);
  return (
    <div
      className={`pivot-chip ${variant ? `pivot-chip--${variant}` : ""}`}
      draggable
      onDragStart={(e) => onDragStart(e, field.key, origin)}
    >
      <ChipIcon field={field} />
      <span className="pivot-chip__label">{field.title}</span>
      {field.isCatalog && field.hierarchical && onChangeDepth && (
        <DepthStepper
          value={depth}
          onChange={(v) => onChangeDepth(field.key, v)}
          title={depthTitle}
        />
      )}
      {onRemove && (
        <button
          type="button"
          className="pivot-chip__remove"
          onClick={() => onRemove(field.key)}
          aria-label="remove"
        >
          ×
        </button>
      )}
    </div>
  );
};

export const PivotFields = ({
  fields,
  assignments,
  onAssign,
  records,
  filterValues,
  onToggleFilterValue,
  catalogDepth,
  onChangeCatalogDepth,
}) => {
  const [t] = useTranslation("exports");
  const depthTitle = t("builder.catalog_depth");
  // Which zone the pointer is currently dragging a chip over (for highlight).
  const [dragOverZone, setDragOverZone] = useState(null);

  const byKey = (key) => fields.find((f) => f.key === key);
  const usedKeys = new Set([
    ...assignments.rows,
    ...assignments.cols,
    ...assignments.filters,
  ]);
  const available = fields.filter((f) => !usedKeys.has(f.key));

  // Source is split into typed blocks, each with its own emphasis styling.
  // Ordered by element count: the smaller sets (fields, flags) sit on top,
  // the larger ones (indicators, catalogs) below.
  const sourceBlocks = [
    {
      variant: "fields",
      label: t("builder.fields"),
      items: available.filter(
        (f) => !f.isCatalog && !f.isIndicator && !f.isTagFlag
      ),
    },
    {
      variant: "flag",
      label: t("builder.source_flags"),
      items: available.filter((f) => f.isTagFlag),
    },
    {
      variant: "indicator",
      label: t("builder.source_indicators"),
      items: available.filter((f) => f.isIndicator),
    },
    {
      variant: "catalog",
      label: t("builder.source_catalogs"),
      items: available.filter((f) => f.isCatalog),
    },
  ];

  const handleDragStart = (e, key, origin) => {
    e.dataTransfer.setData("field", key);
    e.dataTransfer.setData("origin", origin);
  };

  const dropHandlers = (zone) => ({
    onDragOver: (e) => {
      e.preventDefault();
      if (dragOverZone !== zone) setDragOverZone(zone);
    },
    onDragLeave: (e) => {
      // Ignore leaves fired while crossing into a child element of the zone.
      if (!e.currentTarget.contains(e.relatedTarget)) setDragOverZone(null);
    },
    onDrop: (e) => {
      e.preventDefault();
      setDragOverZone(null);
      const key = e.dataTransfer.getData("field");
      if (key) onAssign(key, zone);
    },
  });

  const chipProps = (field, origin, withRemove) => ({
    field,
    origin,
    onDragStart: handleDragStart,
    onRemove: withRemove ? (key) => onAssign(key, "available") : undefined,
    depth: field.isCatalog ? catalogDepth(field.key) : undefined,
    onChangeDepth: onChangeCatalogDepth,
    depthTitle,
  });

  const Zone = ({ zone, title, keys }) => (
    <div
      className={`pivot-zone pivot-zone--${zone}${
        dragOverZone === zone ? " pivot-zone--dragover" : ""
      }`}
      {...dropHandlers(zone)}
    >
      <Typography className="pivot-zone__title">
        <Icon
          name={ZONE_ICON[zone].name}
          color={ZONE_ICON[zone].color}
          size={14}
          className="pivot-zone__icon"
        />
        {title}
      </Typography>
      <div className="pivot-zone__body">
        {keys.length === 0 ? (
          <span className="pivot-zone__hint">{t("builder.drag_hint")}</span>
        ) : (
          keys.map((k) => (
            <FieldChip key={k} {...chipProps(byKey(k), zone, true)} />
          ))
        )}
      </div>
    </div>
  );

  return (
    <Box className="builder-panel pivot-fieldbar dyTheme1 dyBorder1 rounded">
      {/* Source: typed blocks (fields / indicators / flags / catalogs).
          The whole area is one drop target for returning chips; a dropped
          chip re-lands in its own block by type. Empty blocks are hidden. */}
      <div className="pivot-source" {...dropHandlers("available")}>
        {sourceBlocks
          .filter((b) => b.items.length > 0)
          .map((b) => (
            <div
              key={b.variant}
              className={`pivot-source__group pivot-source__group--${b.variant}`}
            >
              <Typography className="pivot-source__label">{b.label}</Typography>
              <div className="pivot-fieldlist">
                {b.items.map((f) => (
                  <FieldChip key={f.key} {...chipProps(f, "available", false)} />
                ))}
              </div>
            </div>
          ))}
      </div>

      {/* Drop zones. Values is fixed (Count/%) so it isn't shown as a zone. */}
      <div className="pivot-zones-row">
        <Zone zone="rows" title={t("builder.rows")} keys={assignments.rows} />
        <Zone zone="cols" title={t("builder.columns")} keys={assignments.cols} />
        <Zone zone="filters" title={t("builder.filters")} keys={assignments.filters} />
      </div>

      {/* Filter value pickers */}
      {assignments.filters.length > 0 && (
        <div className="pivot-filtervalues-row">
          {assignments.filters.map((key) => {
            const options = distinctValues(records, key);
            const selected = filterValues[key];
            return (
              <div key={key} className="pivot-filtervalues__group">
                <Typography className="pivot-filtervalues__label">
                  {byKey(key)?.title}
                </Typography>
                <div className="pivot-filtervalues__options">
                  {options.map((opt) => {
                    const checked =
                      !selected || selected.size === 0 || selected.has(opt);
                    return (
                      <label key={opt} className="pivot-filtervalues__option">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => onToggleFilterValue(key, opt, options)}
                        />
                        <span>{opt}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Box>
  );
};
